import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { ReservationDetailView } from "@/components/shop/ReservationDetailView";
import { fetchBuyerReservation } from "@/features/buyerAuth";
import { type ReservationStatus } from "@/features/storefront";

// TNK-167 phase C — account-scoped reservation detail. Same view as the public
// /shop/r/:token page (shared ReservationDetailView), but loaded by id through
// the buyer-gated endpoint: 404 → not found, 403 → belongs to another account,
// 401 → bounce to login and return here after sign-in.
export const Route = createFileRoute("/shop/account/reservations/$id")({
  component: ShopAccountReservationDetail,
});

type State =
  | { phase: "loading" }
  | { phase: "ready"; reservation: ReservationStatus; token: string }
  | { phase: "notfound" }
  | { phase: "forbidden" }
  | { phase: "error" };

function ShopAccountReservationDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchBuyerReservation(id);
      if (cancelled) return;
      if (result.ok) {
        setState({
          phase: "ready",
          reservation: result.reservation,
          token: result.reservation.statusToken,
        });
      } else if (result.reason === "auth") {
        void navigate({
          to: "/shop/account/login",
          search: { return: `/shop/account/reservations/${id}` },
        });
      } else if (result.reason === "notfound") {
        setState({ phase: "notfound" });
      } else if (result.reason === "forbidden") {
        setState({ phase: "forbidden" });
      } else {
        setState({ phase: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (state.phase === "loading") {
    return <p className="py-12 text-center text-muted-foreground">Loading…</p>;
  }
  if (state.phase === "notfound") {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <p className="text-muted-foreground">Reservation not found.</p>
        <BackLink />
      </div>
    );
  }
  if (state.phase === "forbidden") {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12 text-center">
        <p className="text-muted-foreground">
          This reservation belongs to a different account.
        </p>
        <BackLink />
      </div>
    );
  }
  if (state.phase === "error") {
    return (
      <p className="py-12 text-center text-destructive">
        Couldn’t load this reservation.
      </p>
    );
  }

  return (
    <ReservationDetailView
      reservation={state.reservation}
      token={state.token}
      onReservationUpdate={(next) =>
        setState({ phase: "ready", reservation: next, token: state.token })
      }
      subtitle="The seller will follow up by email."
      footer={<BackLink />}
    />
  );
}

function BackLink() {
  return (
    <Link
      to="/shop/account"
      className="inline-block text-sm font-medium text-primary hover:underline"
    >
      ← Back to your reservations
    </Link>
  );
}
